/**
 * RIM — In-House Transfer Flow (4-step wizard).
 *
 * Wave 5.3 IRA-FE-TRANSFERS-AND-BENEFMOD:
 *   POSTs to /v3/ira/post-transfer-internal. The Function:
 *     • Probes both source + destination plans (must exist + active, must
 *       differ).
 *     • Resolves the TC sequence from the Q4 admin-config'd
 *       IraTransactionCode catalog (scenario IN_HOUSE_TRANSFER). Empty
 *       catalog → friendly TC_NOT_CONFIGURED 422 with userVisible=true so
 *       this wizard surfaces a "Transaction code sequence pending — contact
 *       admin" banner in place of a 500.
 *
 * Steps:
 *   1. Source plan summary (humanized title from useIraDetail).
 *   2. Destination plan number + amount + full/partial.
 *   3. Reason + review.
 *   4. Submit → success / error.
 *
 * No GUIDs / Cart IDs / raw codes surface — TC is only displayed in the
 * confirmation banner.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  KVPair,
  Pill,
  Stepper,
  WidgetCard,
} from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import type {
  IraTransferErrorCode,
  IraTransferPostedData,
} from '@retail-ops/shared-types';
import '../styles/rim.css';

interface InHouseDraft {
  destinationPlanAccountNumber: string;
  amount: string;
  isFullTransfer: boolean;
  taxYear: number;
  transferReason: string;
  officerNotes: string;
}

const STEPS = [
  { label: 'Source plan' },
  { label: 'Destination + amount' },
  { label: 'Review' },
  { label: 'Submit' },
];

const IN_HOUSE_ERROR_TEXT: Record<IraTransferErrorCode, string> = {
  MISSING_DESTINATION_INSTITUTION:
    'Destination plan is required.',
  MISSING_DESTINATION_PLAN:
    'Destination plan number is required.',
  MISSING_SOURCE_PLAN:
    'Source plan number is required.',
  SAME_PLAN_TRANSFER:
    'Source and destination plan numbers must differ.',
  MISSING_TRANSFER_REASON:
    'Transfer reason is required.',
  TC_NOT_CONFIGURED:
    'Transaction code sequence pending — contact admin. An administrator must seed the IRA Transaction Codes catalog (scenario IN_HOUSE_TRANSFER) before this transfer can post.',
  PLAN_INACTIVE:
    'One of the plans is not active in Core Director.',
  PLAN_NOT_FOUND:
    'Source or destination plan was not found in Core Director.',
  INVALID_AMOUNT:
    'Transfer amount must be a positive number.',
  MISSING_PLAN_NUMBER:
    'Source plan number is required.',
  MISSING_OFFICER:
    'Officer code is required.',
  POST_FAILED:
    'Core Director rejected the transfer post.',
  INVALID_BODY:
    'The transfer request body was invalid.',
};

export function InHouseTransferFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<InHouseDraft>({
    destinationPlanAccountNumber: '',
    amount: '',
    isFullTransfer: false,
    taxYear: currentYear,
    transferReason: '',
    officerNotes: '',
  });

  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<IraTransferErrorCode | null>(null);
  const [postedData, setPostedData] = useState<IraTransferPostedData | null>(null);

  const amountNum = Number.parseFloat(draft.amount);

  if (!planNumber) {
    return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before starting an in-house transfer." />;
  }
  if (loadingIra) {
    return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  }
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const sameAsSource =
    draft.destinationPlanAccountNumber.trim().length > 0
    && draft.destinationPlanAccountNumber.trim() === ira.planNumber;

  const canProceed = ((): boolean => {
    switch (step) {
      case 0: return true;
      case 1:
        return draft.destinationPlanAccountNumber.trim().length > 0
          && !sameAsSource
          && Number.isFinite(amountNum) && amountNum > 0;
      case 2: return submitState === 'idle' || submitState === 'error';
      case 3: return false;
      default: return false;
    }
  })();

  const submit = async (): Promise<void> => {
    if (!ira.cifId) {
      setSubmitError('Plan is missing the owner CIF — cannot post transfer.');
      setSubmitErrorCode('MISSING_PLAN_NUMBER');
      setSubmitState('error');
      return;
    }
    if (!user?.officerCode) {
      setSubmitError('Officer code is required to post the transfer.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }
    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    setPostedData(null);

    try {
      const resp = await client.retail.ira.postInHouseTransfer({
        cif: ira.cifId,
        sourcePlanAccountNumber: ira.planNumber,
        destinationPlanAccountNumber: draft.destinationPlanAccountNumber.trim(),
        amount: amountNum,
        isFullTransfer: draft.isFullTransfer,
        taxYear: draft.taxYear,
        transferReason: draft.transferReason.trim(),
        officerCode: user.officerCode,
        ...(draft.officerNotes.trim() ? { officerNotes: draft.officerNotes.trim() } : {}),
      });

      if (!resp.success) {
        setSubmitErrorCode(resp.code);
        setSubmitError(IN_HOUSE_ERROR_TEXT[resp.code] ?? resp.error ?? resp.message ?? 'In-house transfer post refused.');
        setSubmitState('error');
        return;
      }

      setPostedData(resp.data);
      emit({
        eventType: 'ira-transfer-internal-posted',
        eventCategory: 'cif-account',
        severity: 'warn',
        context: `plan:${ira.planNumber}`,
        payload: {
          sourcePlanAccountNumber: resp.data.sourcePlanAccountNumber,
          destinationPlanAccountNumber: resp.data.destinationPlanAccountNumber,
          amount: amountNum,
          isFullTransfer: resp.data.isFullTransfer,
          transactionCode: resp.data.transactionCode,
          scenario: resp.data.scenario,
        },
      });
      setSubmitState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'In-house transfer post failed.');
      setSubmitErrorCode(null);
      setSubmitState('error');
    }
  };

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>IRA in-house transfer · {ira.planDescription ?? ira.planCodeRaw} · {ira.planNumber}</h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Source plan" stripe="compliance">
          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${ira.planDescription ?? ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="Owner CIF" value={ira.cifId ?? '—'} />
            {ira.balance != null && (
              <KVPair label="Current balance" value={`$${ira.balance.toFixed(2)}`} />
            )}
          </div>
          <div className="rim-flow__hint">
            Funds will move to another PCB IRA plan. Transfer codes resolve from the
            admin-editable IRA Transaction Codes catalog (scenario IN_HOUSE_TRANSFER).
          </div>
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Destination plan + amount" stripe="compliance">
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Destination plan number</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.destinationPlanAccountNumber}
                placeholder="10-digit PCB IRA plan number"
                onChange={(e) => setDraft({ ...draft, destinationPlanAccountNumber: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Amount (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.amount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, amount: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Tax year</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.taxYear}
                min={1900}
                max={9999}
                onChange={(e) => setDraft({ ...draft, taxYear: Number.parseInt(e.currentTarget.value, 10) || draft.taxYear })}
              />
            </label>
            <label className="rim-flow__checkbox">
              <input
                type="checkbox"
                checked={draft.isFullTransfer}
                onChange={(e) => setDraft({ ...draft, isFullTransfer: e.currentTarget.checked })}
              />
              <span>Full transfer (closes the source plan)</span>
            </label>
          </div>
          {sameAsSource && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <Pill variant="warn">Same plan</Pill> Source and destination plan numbers must differ.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Reason + review" stripe="compliance">
          <label className="rim-flow__field">
            <span className="rim-flow__field-label">Transfer reason</span>
            <input
              type="text"
              className="rim-benef-field"
              value={draft.transferReason}
              placeholder="e.g. Cross-product move; SIMPLE → Trad after 2-yr"
              onChange={(e) => setDraft({ ...draft, transferReason: e.currentTarget.value })}
            />
          </label>
          <label className="rim-flow__field">
            <span className="rim-flow__field-label">Officer notes (optional)</span>
            <input
              type="text"
              className="rim-benef-field"
              value={draft.officerNotes}
              onChange={(e) => setDraft({ ...draft, officerNotes: e.currentTarget.value })}
            />
          </label>
          <div className="rim-kv-grid">
            <KVPair label="Source plan" value={`${ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="Destination plan" value={draft.destinationPlanAccountNumber || '—'} />
            <KVPair label="Amount" value={Number.isFinite(amountNum) ? `$${amountNum.toFixed(2)}` : '—'} />
            <KVPair label="Full transfer" value={draft.isFullTransfer ? 'Yes — closes source plan' : 'No — partial'} />
            <KVPair label="Tax year" value={draft.taxYear} />
          </div>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Submit transfer" stripe="compliance">
          {submitState === 'idle' && (
            <p className="rim-flow__hint">Click "Submit transfer" to post the transaction.</p>
          )}
          {submitState === 'submitting' && <LoadingSpinner label="Posting transfer…" />}
          {submitError && (
            <div className="rim-rmd-banner is-warn" role="alert">
              {submitErrorCode === 'TC_NOT_CONFIGURED' && (
                <Pill variant="warn">TC catalog empty</Pill>
              )}
              {submitErrorCode && submitErrorCode !== 'TC_NOT_CONFIGURED' && (
                <Pill variant="warn">{submitErrorCode}</Pill>
              )}
              {' '}{submitError}
            </div>
          )}
          {submitState === 'submitted' && postedData && (
            <div className="rim-rmd-banner" role="status">
              Transfer posted via TaxPlnTranAdd · TC <strong>{postedData.transactionCode}</strong>
              {' '}· scenario <strong>In-house transfer</strong>.
            </div>
          )}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button
            variant="primary"
            disabled={submitState === 'submitting'}
            onClick={() => { void submit(); }}
          >
            {submitState === 'submitting' ? 'Posting…' : 'Submit transfer'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
